import { inject, Injectable, Signal } from '@angular/core';
import { OKTA_AUTH, OktaAuthStateService } from '@okta/okta-angular';
import { AuthState, OktaAuth } from '@okta/okta-auth-js';
import { AuthService } from '@auth0/auth0-angular';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  catchError,
  distinctUntilChanged,
  EMPTY,
  filter,
  firstValueFrom,
  map,
  Observable,
  switchMap,
} from 'rxjs';
import { ApiPartnerEnum } from '@claas-rdv/rdv-client';

/**
 * Unified authentication wrapper that supports both Okta (SOFF / Händler)
 * and Auth0 (CC3 / Kunden) for Native-to-Web SSO.
 *
 * ## Okta Flow (Session Token Exchange):
 * 1. Native app obtains a short-lived sessionToken via Okta Authn API
 * 2. Web app receives sessionToken as URL param on /security/okta/login
 * 3. `exchangeOktaSessionToken()` calls signInWithRedirect → redirects to Okta /authorize
 * 4. Okta sets session cookie and redirects back to /security/okta/login?code=&state=
 * 5. InboundComponent detects the callback via `isOktaCallback()` and calls `handleOktaCallback()`
 * 6. `handleOktaCallback()` exchanges the authorization code for tokens via `token.parseFromUrl()`
 * 7. Returns the access token and the stored originalUri (with machine/autoConnect params)
 *
 * ## Auth0 Flow — Session Transfer Token (preferred):
 * 1. Native app exchanges its refresh token for a session_transfer_token
 * 2. Native app opens web app: /security/auth0/login?session_transfer_token=...&organization=...
 * 3. Web app calls `getAuth0TokenSilently({ session_transfer_token, organization })`
 * 4. Auth0 validates the token, establishes a session, returns an access token
 *
 * ## Auth0 Flow — SSO Cookie (fallback when no session_transfer_token in URL):
 * 1. Native app logged in via system browser → Auth0 SSO cookie set
 * 2. Web app calls `getAuth0TokenSilently()` → Auth0 recognizes SSO session via cookie
 * 3. If silent auth fails → `loginWithRedirect()` as last resort
 */
@Injectable({ providedIn: 'root' })
export class AuthWrapperService {
  // Okta
  private oktaAuth = inject<OktaAuth>(OKTA_AUTH);
  private oktaAuthStateService = inject(OktaAuthStateService);

  // Auth0
  private auth0AuthService = inject(AuthService);

  // Cached token signals per partner
  private accessTokenSignals = new Map<ApiPartnerEnum, Signal<string | null>>();

  // ---------------------------------------------------------------------------
  // Okta: Session Token Exchange (Native-to-Web SSO)
  // ---------------------------------------------------------------------------

  /**
   * Exchanges an Okta sessionToken (obtained by the native app) for a full
   * Okta session. This triggers a browser redirect to the Okta /authorize
   * endpoint, which sets the session cookie and redirects back with an
   * authorization code to the configured redirectUri.
   *
   * The `originalUri` is stored by the Okta SDK in sessionStorage. After
   * the callback is handled, `handleOktaCallback()` retrieves it to extract
   * the machine and autoConnect params.
   *
   * @param sessionToken Short-lived token from Okta Authn API (5 min validity)
   * @param originalUri  The URI to return to after auth (includes machine/autoConnect params)
   */
  async exchangeOktaSessionToken(sessionToken: string, originalUri: string): Promise<void> {
    await this.oktaAuth.signInWithRedirect({ sessionToken, originalUri });
  }

  /**
   * Exchanges an Okta interclient_token for a full Okta session.
   * This triggers a redirect to Okta /authorize with the interclient_token
   * passed as an extra parameter. Okta validates the token and redirects
   * back with an authorization code.
   *
   * @param interclientToken  The interclient token from the URL
   * @param originalUri       The URI to return to after auth (includes machine/autoConnect params)
   */
  async exchangeOktaInterclientToken(interclientToken: string, originalUri: string): Promise<void> {
    this.oktaAuth.setOriginalUri(originalUri);
    await this.oktaAuth.token.getWithRedirect({
      responseType: ['code'],
      extraParams: {
        interclient_token: interclientToken,
      },
    });
  }

  /**
   * Checks whether the user is already authenticated with Okta and returns
   * the access token if available. Used as a safety net when InboundComponent
   * loads without a sessionToken or callback params but the user might
   * already have a valid Okta session (e.g. direct navigation).
   *
   * @returns The access token string, or null if not authenticated
   */
  async getOktaTokenIfAuthenticated(): Promise<string | null> {
    const isAuthenticated = await this.oktaAuth.isAuthenticated();
    if (isAuthenticated) {
      const token = this.oktaAuth.getAccessToken();
      return token ?? null;
    }
    return null;
  }

  /**
   * Handles the Okta authorization code callback directly.
   *
   * Called when InboundComponent detects `?code=` and `?state=` in the URL,
   * which means Okta has redirected back after /authorize with an auth code.
   *
   * This method:
   * 1. Calls `token.parseFromUrl()` which reads ?code= and ?state= from the URL,
   *    looks up the matching PKCE transaction in sessionStorage, and exchanges
   *    the authorization code for tokens via the Okta token endpoint.
   * 2. Stores the tokens in the Okta TokenManager.
   * 3. Updates the auth state so `isAuthenticated()` returns true.
   * 4. Retrieves and returns the stored `originalUri` (which contains
   *    machine/autoConnect params set by `exchangeOktaSessionToken`).
   *
   * We handle this manually instead of using `handleLoginRedirect()` because:
   * - `handleLoginRedirect()` depends on `isLoginRedirect()` which has a strict
   *   URL prefix matching check that can fail in edge cases.
   * - `handleLoginRedirect()` calls `restoreOriginalUri` which triggers Angular
   *   Router navigation back to the same route — causing a loop since the
   *   redirectUri IS the InboundComponent route.
   *
   * @returns Object with accessToken and originalUri (path with query params)
   */
  async handleOktaCallback(): Promise<{ accessToken: string; originalUri: string | undefined }> {
    // Exchange the authorization code for tokens.
    // parseFromUrl reads ?code= and ?state= from window.location.search,
    // validates the state against the stored PKCE transaction, then calls
    // the /token endpoint to exchange the code for id_token + access_token.
    const { tokens } = await this.oktaAuth.token.parseFromUrl();

    // Store tokens in the Okta TokenManager
    this.oktaAuth.tokenManager.setTokens(tokens);

    // Update the auth state so isAuthenticated() and authState$ emit correctly
    await this.oktaAuth.authStateManager.updateAuthState();

    // Retrieve the originalUri that was stored by signInWithRedirect().
    // This was set in exchangeOktaSessionToken() and contains the path
    // with machine and autoConnect query params.
    const originalUri = this.oktaAuth.getOriginalUri() ?? undefined;

    // Clean up stored transaction/URI state
    this.oktaAuth.removeOriginalUri();

    // Extract the access token
    const accessToken = this.oktaAuth.getAccessToken();
    if (!accessToken) {
      throw new Error('Okta callback succeeded but no access token available');
    }

    return { accessToken, originalUri };
  }

  // ---------------------------------------------------------------------------
  // Okta: Detect callback
  // ---------------------------------------------------------------------------

  /**
   * Checks whether the current URL looks like an Okta authorization code callback.
   * This is a simple URL param check that does NOT rely on the Okta SDK's
   * `isLoginRedirect()` which requires a strict URL prefix match.
   */
  isOktaCallback(): boolean {
    const params = new URLSearchParams(window.location.search);
    return params.has('code') && params.has('state');
  }

  // ---------------------------------------------------------------------------
  // Auth0: Silent Auth (SSO Cookie) + Fallback
  // ---------------------------------------------------------------------------

  /**
   * Attempts to obtain an Auth0 access token silently via SSO cookie or
   * session transfer token.
   *
   * When a `session_transfer_token` is provided in `authorizationParams`, Auth0
   * exchanges it for a session on the `/authorize` call without prompting the
   * user for credentials. When only the SSO cookie is available, the existing
   * cookie-based silent auth continues to work unchanged.
   *
   * When an `organization` is provided, it is forwarded to the `/authorize`
   * request. This is required by Auth0 when the session transfer token was
   * issued with an organization context — a mismatch causes Auth0 to reject
   * the token and force re-authentication.
   *
   * @param authorizationParams  Optional extra params forwarded to /authorize
   *                             (e.g. `session_transfer_token`, `organization`)
   * @throws Error with `error: 'login_required'` if no SSO session exists
   */
  async getAuth0TokenSilently(authorizationParams?: Record<string, string>): Promise<string> {
    const token = await firstValueFrom(
      this.auth0AuthService.getAccessTokenSilently(
        authorizationParams && Object.keys(authorizationParams).length > 0
          ? { authorizationParams }
          : undefined
      )
    );
    if (!token) {
      throw new Error('Auth0 did not return an access token');
    }
    return token;
  }

  // ---------------------------------------------------------------------------
  // Generic: Login Redirect (fallback when no session exists)
  // ---------------------------------------------------------------------------

  /**
   * Triggers a full login redirect for the given partner system.
   * Used as fallback when silent auth / session token exchange is not possible.
   *
   * For Auth0 (CC3), `authorizationParams` are forwarded directly to the
   * `/authorize` request. This is how `organization` is propagated when the
   * silent auth path was not available (e.g. no SSO cookie, no session transfer
   * token). The `session_transfer_token` should NOT be forwarded here — it is
   * single-use and was already consumed (or absent) before reaching this fallback.
   *
   * @param apiPartner          The API partner to authenticate against
   * @param appState            Optional state preserved across the redirect
   *                            (e.g. machine, autoConnect, organization)
   * @param authorizationParams Optional extra params forwarded to /authorize
   *                            (e.g. `organization`)
   */
  async loginWithRedirect(
    apiPartner: ApiPartnerEnum,
    appState?: Record<string, string>,
    authorizationParams?: Record<string, string>,
    originalUri?: string
  ): Promise<void> {
    if (apiPartner === ApiPartnerEnum.SOFF) {
      await this.oktaAuth.signInWithRedirect({ originalUri });
    } else if (apiPartner === ApiPartnerEnum.CC3) {
      this.auth0AuthService.loginWithRedirect({
        appState: appState ?? {},
        ...(authorizationParams && Object.keys(authorizationParams).length > 0
          ? { authorizationParams }
          : {}),
      });
    } else {
      console.error('loginWithRedirect: apiPartner not supported:', apiPartner);
    }
  }

  // ---------------------------------------------------------------------------
  // Token Signal (reactive access to the current token)
  // ---------------------------------------------------------------------------

  /**
   * Returns a Signal that reactively provides the current access token
   * for the given partner system. Useful for components that need to
   * continuously track authentication state.
   */
  getTokenSignal(apiPartner: ApiPartnerEnum): Signal<string | null> {
    let sig = this.accessTokenSignals.get(apiPartner);
    if (!sig) {
      sig = toSignal(this.token$(apiPartner), { initialValue: null });
      this.accessTokenSignals.set(apiPartner, sig);
    }
    return sig;
  }

  // ---------------------------------------------------------------------------
  // Private: Token Observables
  // ---------------------------------------------------------------------------

  private token$(apiPartner: ApiPartnerEnum): Observable<string> {
    if (apiPartner === ApiPartnerEnum.SOFF) {
      return this.oktaAuthStateService.authState$.pipe(
        filter((s: AuthState) => !!s && !!s.isAuthenticated),
        map((s: AuthState) => s.accessToken?.accessToken),
        filter((t): t is string => !!t)
      );
    } else if (apiPartner === ApiPartnerEnum.CC3) {
      return this.auth0AuthService.isAuthenticated$.pipe(
        distinctUntilChanged(),
        filter((isAuth) => isAuth),
        switchMap(() =>
          this.auth0AuthService.getAccessTokenSilently().pipe(
            catchError((err) => {
              if (err.error === 'login_required') {
                // No organization context is available here since this reactive
                // path is triggered by auth state changes, not a fresh navigation.
                // Fall back to a plain redirect without organization — the user
                // will re-authenticate and organization will be set from the URL
                // params on the next InboundComponent load.
                this.auth0AuthService.loginWithRedirect();
              }
              return EMPTY;
            })
          )
        ),
        filter((t): t is string => !!t)
      );
    }

    console.error('token$: apiPartner not supported:', apiPartner);
    return EMPTY;
  }
}
